import React, { useState, useEffect } from 'react';

const heroImages = [
  '/images/hero-banner.jpg',
  '/images/hero-banner-2.jpg',
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const scrollToOrder = () => {
    document.getElementById("order").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="w-full bg-green-50 mt-4 pb-8">
      <div className="max-w-4xl mx-auto px-4 text-center">
        {/* Hero Banner Slider */}
        <div className="relative overflow-hidden rounded-xl shadow-xl">
          <div
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {heroImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Hero Banner ${idx + 1}`}
                className="w-full h-auto object-cover shrink-0"
              />
            ))}
          </div>

          {/* Slider Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`transition-all duration-500 rounded-full ${
                  currentSlide === idx
                    ? 'w-7 h-2.5 bg-white shadow-md'
                    : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
        
        {/* ===== Highlighted Age Badge — Premium & Eye-Catching ===== */}
        <div className="mt-10 mb-8 mx-auto max-w-[95%] md:max-w-2xl relative">
          {/* Pulsing ring glow behind */}
          <div 
            className="absolute inset-0 rounded-2xl md:rounded-3xl -z-10"
            style={{animation: 'glow-pulse-green 3s ease-in-out infinite'}}
          ></div>

          {/* Animated gradient border wrapper */}
          <div 
            className="rounded-2xl md:rounded-3xl p-[3px] relative"
            style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #86efac, #22c55e, #16a34a)',
              backgroundSize: '300% 300%',
              animation: 'gradient-shift 4s ease infinite'
            }}
          >
            {/* Outer glow */}
            <div 
              className="absolute -inset-1 rounded-[1.25rem] md:rounded-[2rem] blur-md opacity-50 -z-10"
              style={{
                background: 'linear-gradient(135deg, #22c55e, #4ade80, #16a34a)',
                backgroundSize: '300% 300%',
                animation: 'gradient-shift 4s ease infinite'
              }}
            ></div>

            {/* Inner content */}
            <div className="relative bg-gradient-to-br from-[#052e16] via-[#0a3d1f] to-[#052e16] rounded-[calc(1rem-1px)] md:rounded-[calc(1.5rem-1px)] px-5 py-4 sm:px-7 sm:py-5 md:px-10 md:py-6 overflow-hidden">
              {/* Background bokeh */}
              <div className="absolute top-0 right-0 w-24 h-24 md:w-40 md:h-40 rounded-full bg-green-500 blur-[60px] opacity-20"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 md:w-32 md:h-32 rounded-full bg-emerald-400 blur-[50px] opacity-15"></div>

              {/* Sparkle decorations */}
              <div className="absolute top-2 right-[12%] text-green-300 text-sm md:text-base" style={{animation: 'sparkle 2s ease-in-out infinite'}}>✦</div>
              <div className="absolute bottom-2 left-[15%] text-emerald-300 text-xs md:text-sm" style={{animation: 'sparkle 2.5s ease-in-out infinite 0.7s'}}>✦</div>
              <div className="absolute top-3 left-[8%] text-lime-300 text-xs" style={{animation: 'sparkle 3s ease-in-out infinite 1.2s'}}>✦</div>

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center text-center gap-1.5 md:gap-2">
                {/* Leaf decorations row */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg md:text-2xl" style={{animation: 'float 3s ease-in-out infinite'}}>🌿</span>
                  <span className="text-green-300/80 text-[10px] md:text-xs font-extrabold tracking-[0.25em] uppercase">বিশ্বস্ত মায়েদের পছন্দ</span>
                  <span className="text-lg md:text-2xl" style={{animation: 'float 3s ease-in-out infinite 0.5s'}}>🌿</span>
                </div>

                {/* Main text */}
                <h2 className="text-[15px] min-[375px]:text-[17px] sm:text-xl md:text-2xl lg:text-3xl font-black leading-snug md:leading-normal">
                  <span 
                    className="text-transparent bg-clip-text inline-block"
                    style={{
                      backgroundImage: 'linear-gradient(90deg, #86efac, #4ade80, #22c55e, #86efac)',
                      backgroundSize: '200% auto',
                      animation: 'text-shimmer 4s linear infinite',
                      WebkitBackgroundClip: 'text',
                    }}
                  >
                    ৬ মাস থেকে ১০ বছরের বাচ্চাদের জন্য
                  </span>
                  <br className="md:hidden" />
                  <span className="text-white font-black drop-shadow-[0_0_15px_rgba(74,222,128,0.4)] block md:inline mt-1 md:mt-0 md:ml-2 text-[17px] min-[375px]:text-[19px] sm:text-2xl md:text-3xl lg:text-[2.1rem]">
                    পুষ্টিকর সেরা খাবার
                  </span>
                </h2>

                {/* Bottom star accent */}
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-px w-8 md:w-14 bg-gradient-to-r from-transparent to-green-500/60"></div>
                  <span className="text-green-400 text-sm">★</span>
                  <div className="h-px w-8 md:w-14 bg-gradient-to-l from-transparent to-green-500/60"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={scrollToOrder}
          className="relative group w-[90%] sm:w-full max-w-sm sm:max-w-md mx-auto block text-center mt-8 sm:mt-12 mb-4 rounded-xl transition-transform duration-300 hover:-translate-y-1"
        >
          {/* Base metallic gradient & shadow layer */}
          <div className="absolute inset-0 bg-gradient-to-b from-green-500 via-green-600 to-green-700 rounded-xl shadow-[0_8px_20px_rgba(22,163,74,0.5)] sm:shadow-[0_10px_30px_rgba(22,163,74,0.6)] group-hover:shadow-[0_12px_25px_rgba(22,163,74,0.7)] sm:group-hover:shadow-[0_15px_40px_rgba(22,163,74,0.8)] transition-shadow duration-300"></div>
          
          {/* Metallic Top Glare */}
          <div className="absolute top-0 inset-x-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent rounded-t-xl pointer-events-none"></div>
          
          {/* Bottom Reflection */}
          <div className="absolute bottom-0 inset-x-0 h-1/4 bg-gradient-to-t from-black/20 to-transparent rounded-b-xl pointer-events-none"></div>

          {/* Animated Shine Effect */}
          <div className="absolute inset-0 overflow-hidden rounded-xl">
             <div className="absolute inset-0 -translate-x-[150%] group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out bg-gradient-to-r from-transparent via-white/50 to-transparent w-full skew-x-[-20deg]"></div>
          </div>

          {/* Button Content */}
          <div className="relative flex items-center justify-center gap-2 sm:gap-3 py-2.5 sm:py-4 px-3 sm:px-6 border border-white/40 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 sm:w-8 sm:h-8 text-white drop-shadow-md">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59" />
            </svg>
            <span className="text-white font-black text-[16px] min-[375px]:text-[17px] sm:text-2xl tracking-wide drop-shadow-[0_2px_2px_rgba(0,0,0,0.6)]">
              অর্ডার করতে এখানে ক্লিক করুন
            </span>
          </div>
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
